/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import React from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Dropdown,
  TextField,
  Checkbox,
  PrimaryButton,
  MessageBar,
  MessageBarType,
} from "@fluentui/react";
import useAxios from "axios-hooks";
import Toast from "../../../../Forms/Toast";

const deleteText = css`
    color: #e00000;
    font-weight: 500;
    margin-bottom: 0px;
`;
const warning = css`
    color: #e00000;
    font-weight: 500;
    font-size: 24px;
    margin-left: 5px;
`;
const container = css`
.ms-Dropdown-container,.ms-TextField{
width:100%;
}
`;
const ddlData = [
  { key: -1, text: "Un-Set (Null)" },
  { key: 1, text: "Required" },
  { key: 0, text: "Not Required" },
];

const crsDisplayModeOptions = [
  { key: 1, text: "Show" },
  { key: 2, text: "Hide" },
  { key: 3, text: "Trigger" },
];

const validationSchema = Yup.object().shape({
  crsTitle: Yup.string().max(200, "Max 200 characters").nullable(),
  shortDescription: Yup.string().max(1000, "Max 1000 characters").nullable(),
  longDescription: Yup.string().max(1000, "Max 1000 characters").nullable(),
  completionHeader: Yup.string().max(1000, "Max 1000 characters").nullable(),
  completionButton: Yup.string().max(255, "Max 255 characters").nullable(),
  completionButtonURL: Yup.string().max(255, "Max 255 characters").nullable(),
});


const CobrandSettingsForm = ({ initialData, fetchData }) => {
    const [showToast, setShowToast] = React.useState(false);

  const [{ data, loading: submitting, error: submitError }, submitForm] = useAxios(
    { url: `courses/crs-settings-update` },
    { manual: true },
  );
  const handleSubmit = async (values) => {
    // Only send text fields that were actually changed by the user.
    // Unchanged fields are sent as null so the sproc preserves existing values
    // (or NULL for fields that inherit from the default CoBrandID IS NULL row).
    const payload = { ...values };
    const textFields = [
      'crsTitle', 'descriptionShort', 'descriptionLong',
      'completionHeader', 'completionDescription',
      'completionButton', 'completionButtonURL'
    ];
    textFields.forEach(field => {
      const currentVal = payload[field] || null;
      const initialVal = initialData[field] || null;
      if (currentVal === initialVal) {
        payload[field] = null;
      }
    });

    // Treat empty crsTitle as null so the default title is inherited
    if (payload.crsTitle !== null && payload.crsTitle.trim() === '') {
      payload.crsTitle = null;
    }

    try {
      const result = await submitForm({
        url: 'courses/crs-settings-update',
        method: 'POST',
        data: { settings: [payload] },
      });
      if (!result.isAxiosError) {
        setShowToast(true);
        fetchData()
      }
    } catch (error) {
      console.error("Form submission failed:", error);
    }
  };

  return (
    <Formik
      initialValues={initialData}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => (
        <Form css={container}>
          {submitError && (
            <MessageBar messageBarType={MessageBarType.error}>{submitError.message}</MessageBar>
          )}
          <p css={deleteText}>Values are mixed across selected cobrands.</p>
          <h4>General Settings</h4>
          <div className='displayflex aligncenter'>
            <Dropdown
              label="Show/Hide/Trigger"
              options={crsDisplayModeOptions}
              selectedKey={values.crsDisplayModeID}
              onChange={(e, option) => setFieldValue("crsDisplayModeID", option.key)}
            />
            {!values.crsDisplayModeIDIsMatch  && <span css={warning}>*</span>}
          </div>
           <div className='displayflex aligncenter'>
            <Dropdown
              label="Course Required"
              options={ddlData}
              defaultSelectedKey={values.isRequired}
              onChange={(e, option) => setFieldValue("isRequired", option.key)}
            />
            </div>
          <div className='displayflex aligncenter'>
            <Dropdown
              label="Passing Score Required"
              options={ddlData}
              defaultSelectedKey={values.isPassingScoreRequired}
              onChange={(e, option) => setFieldValue("isPassingScoreRequired", option.key)}
            />
            {!values.isPassingScoreRequiredIsMatch  && <span css={warning}>*</span>}
            </div>
          <div className='displayflex aligncenter'>
            <Dropdown
              label="Steps Required"
              options={ddlData}
              defaultSelectedKey={values.isRequiredSteps}
              onChange={(e, option) => setFieldValue("isRequiredSteps", option.key)}
            />
            {!values.isRequiredStepsIsMatch  && <span css={warning}>*</span>}
            </div>
          <div className='displayflex aligncenter'>
            <Dropdown
              label="Course ID Capture"
              options={ddlData}
              defaultSelectedKey={values.courseIDCaptureOn}
              onChange={(e, option) => setFieldValue("courseIDCaptureOn", option.key)}
            />
            {!values.courseIDCaptureIsMatch  && <span css={warning}>*</span>}
            </div>
           
          <div className='displayflex aligncenter'>
            <Checkbox
              className="mt20"
              label="Don't Show Wrong Answers"
              checked={values.hideWrongAnswers}
              onChange={(e, checked) => setFieldValue("hideWrongAnswers", checked)}
            />
            {!values.hideWrongAnswersIsMatch  && <span css={warning}>*</span>}
            </div>
          <div className='displayflex aligncenter'>
            <Checkbox
              className="mt5"
              label="Hide right & wrong smiley faces"
              checked={values.hideRightWrongSmileFaces}
              onChange={(e, checked) => setFieldValue("hideRightWrongSmileFaces", checked)}
            />
            {!values.hideRightWrongSmileFacesIsMatch  && <span css={warning}>*</span>}
            </div>
          <div className='displayflex aligncenter'>
            <Checkbox
              className="mt5"
              label="Disable back button"
              checked={values.disableBackButton}
              onChange={(e, checked) => setFieldValue("disableBackButton", checked)}
            />
            {!values.disableBackButtonIsMatch  && <span css={warning}>*</span>}
            </div>
          <div className='displayflex aligncenter mb20'>
            <Checkbox
              className="mt5"
              label="Retake only, no review"
              checked={values.retakeOnly}
              onChange={(e, checked) => setFieldValue("retakeOnly", checked)}
            />
            {!values.retakeOnlyIsMatch  && <span css={warning}>*</span>}
            </div>
          <div className='displayflex aligncenter mb20'>
            <div>
              <Checkbox
                className="mt5"
                label="Hide from course list"
                checked={values.hiddenFromCourseList}
                onChange={(e, checked) => setFieldValue("hiddenFromCourseList", checked)}
              />
              <p style={{ fontSize: '12px', color: '#605e5c', margin: '4px 0 0 0' }}>
                When checked, this course will not appear in the course list on the website for the selected cobrands.
              </p>
            </div>
            {!values.hiddenFromCourseListIsMatch  && <span css={warning}>*</span>}
            </div>
          <h4>About the Course</h4>
          <div className='displayflex aligncenter'>
            <TextField
              label="Course Title"
              maxLength={200}
              value={values.crsTitle}
              onChange={(e, newValue) => setFieldValue("crsTitle", newValue)}
            />
            {!values.courseTitleIsMatch  && <span css={warning}>*</span>}
            </div>
          <ErrorMessage name="crsTitle" component="div" className="error-message" />
          <div className='displayflex aligncenter'>
            <TextField
              label="Short Description"
              multiline
              maxLength={1000}
              value={values.descriptionShort}
              onChange={(e, newValue) => setFieldValue("descriptionShort", newValue)}
            />
            {!values.shortDescriptionIsMatch  && <span css={warning}>*</span>}
            </div>
          <ErrorMessage name="descriptionShort" component="div" className="error-message" />
          <div className='displayflex aligncenter'>
            <TextField
              label="Long Description"
              multiline
              maxLength={1000}
              value={values.descriptionLong}
              onChange={(e, newValue) => setFieldValue("descriptionLong", newValue)}
            />
            {!values.longDescriptionIsMatch  && <span css={warning}>*</span>}
            </div>
          <ErrorMessage name="descriptionLong" component="div" className="error-message" />
          <h4>Completion Message</h4>
          <div className='displayflex aligncenter'>
            <TextField
              label="Header"
              maxLength={1000}
              value={values.completionHeader}
              onChange={(e, newValue) => setFieldValue("completionHeader", newValue)}
            />
            {!values.completionHeaderIsMatch  && <span css={warning}>*</span>}
            </div>
          <ErrorMessage name="completionHeader" component="div" className="error-message" />
          <div className='displayflex aligncenter'>
            <TextField
              label="Paragraph(s)"
              multiline
              maxLength={1000}
              value={values.completionDescription}
              onChange={(e, newValue) => setFieldValue("completionDescription", newValue)}
            />
            {!values.completionDescriptionIsMatch  && <span css={warning}>*</span>}
            </div>
          <div className='displayflex aligncenter'>
            <TextField
              label="Button Text"
              maxLength={255}
              value={values.completionButton}
              onChange={(e, newValue) => setFieldValue("completionButton", newValue)}
            />
            {!values.completionButtonIsMatch  && <span css={warning}>*</span>}
            </div>
          <ErrorMessage name="completionButton" component="div" className="error-message" />
          <div className='displayflex aligncenter'>
            <TextField
              label="Button URL"
              maxLength={1000}
              value={values.completionButtonURL}
              onChange={(e, newValue) => setFieldValue("completionButtonURL", newValue)}
            />
            {!values.completionButtonURLIsMatch  && <span css={warning}>*</span>}
            </div>
          <ErrorMessage name="completionButtonURL" component="div" className="error-message" />
          <div className='displayflex aligncenter'>
            <Dropdown
              label="Button URL Target"
              options={[{ key: true, text: "New Tab" }, { key: false, text: "Same Tab" }]}
              selectedKey={values.completionButtonNewTab}
              onChange={(e, option) => setFieldValue("completionButtonNewTab", option.key)}
            />
            {!values.completionButtonNewTabIsMatch  && <span css={warning}>*</span>}
            </div>
          <p css={deleteText} className='mt20'>To remove a value you must enter: &lt;&lt;DELETE&gt;&gt;</p>
          <p css={deleteText}>Empty fields will not be updated/changed</p>
          <PrimaryButton type="submit" text="Submit" style={{ marginTop: "20px" }} disabled={submitting} />
          <Toast show={showToast} message="Settings Saved" onClose={()=>setShowToast(false)}/>

        </Form>
      )}
    </Formik>
  );
};

export default CobrandSettingsForm;
